// src/services/OffersManager.js - FIXED: Combo items use item_id instead of combo_id

class OffersManager {
  constructor(apiClient) {
    this.apiClient = apiClient;

    // ✅ CRITICAL: These should ONLY contain what API returns
    this.appliedOffers = [];
    this.autoAppliedOffers = [];
    this.availableOffers = [];
    this.cartAnalysis = null;
    this.listeners = [];
    this.activeOffersReference = null;

    // Track manually removed auto-offers
    this.removedAutoOfferIds = new Set();

    // Cache for optimization
    this._appliedOfferIds = new Set();
    this._preparedCartCache = null;
    this._cartCacheKey = null;

    // Debounce re-analysis
    this._pendingAnalysis = null;
    this._analysisDebounceMs = 300;

    // Notification callback
    this.showNotification = null;

    // ✅ NEW: Cache clear timestamp to track invalidation
    this._cacheInvalidationTime = Date.now();
  }

  // ✅ NEW: Method to clear cache when calculation logic changes
  clearCache() {
    this._preparedCartCache = null;
    this._cartCacheKey = null;
    this._cacheInvalidationTime = Date.now();
    console.log('🧹 OffersManager cache cleared');
  }

  setNotificationCallback(callback) {
    this.showNotification = callback;
  }

  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  notifyListeners() {
    if (!this._notificationPending) {
      this._notificationPending = true;
      Promise.resolve().then(() => {
        this._notificationPending = false;
        const state = this.getState();
        this.listeners.forEach(callback => callback(state));
      });
    }
  }

  getState() {
    return {
      appliedOffers: this.appliedOffers,
      availableOffers: this.availableOffers,
      autoAppliedOffers: this.autoAppliedOffers,
      cartAnalysis: this.cartAnalysis,
      removedAutoOfferIds: Array.from(this.removedAutoOfferIds),
    };
  }

  async analyzeCart(cartItems, branchId, customerId, options = {}) {
    console.group('🔍 [OffersManager.analyzeCart] Starting');
    console.log('Input:', {
      itemsCount: cartItems?.length || 0,
      branchId,
      customerId,
      appliedOffers: this.appliedOffers.map(o => ({ id: o.id, name: o.name, discount: o.discount_amount })),
      autoAppliedOffers: this.autoAppliedOffers.map(o => ({ id: o.id, name: o.name, discount: o.discount_amount }))
    });

    // ✅ DEBUG: Log each item's pricing details
    console.group('🔍 RECEIVED ITEMS DETAILS:');
    cartItems?.forEach((item, idx) => {
      console.log(`Item ${idx + 1}:`, {
        name: item.name,
        id: item.id,
        price: item.price,
        flatPrice: item.flatPrice,
        hasVariations: !!item.variations?.length,
        variationCount: item.variations?.length || 0,
        variations: item.variations?.map(v => ({ name: v.variation_name, price: v.variation_price })) || [],
        quantity: item.quantity
      });
    });
    console.groupEnd();

    if (!cartItems || cartItems.length === 0) {
      console.log('⚠️ Empty cart, clearing offers');
      this.clearOffers();
      this.notifyListeners();
      console.groupEnd();
      return null;
    }

    this._logCartPricing(cartItems);

    try {
      const items = this._prepareCartItems(cartItems);

      const payload = {
        branch_id: Number(branchId),
        items: items,
        customer_id: Number(customerId),
      };

      // Include address info for ShipRocket delivery fee calculation
      if (options.address_id) {
        payload.address_id = Number(options.address_id);
      } else if (options.customer_pincode) {
        payload.customer_pincode = String(options.customer_pincode);
      }

      if (options.order_type) {
        payload.order_type = Number(options.order_type);
      }

      // Courier speed the customer picked ('air' | 'surface') — the backend prices the delivery
      // fee for that speed when it is available on this route.
      if (options.delivery_speed === 'air' || options.delivery_speed === 'surface') {
        payload.delivery_speed = options.delivery_speed;
      }

      // ✅ Redeem Points — when the customer has entered a positive amount in
      // the cart's Redeem Points input, ask the backend to compute the loyalty
      // discount as part of this analysis so the cart can show it live and
      // reduce the displayed total (mirrors what /web/order already does at
      // order-creation). The discount is always computed server-side.
      if (Number(options.loyalty_points_used) > 0) {
        payload.loyalty_points_used = Number(options.loyalty_points_used);
      }

      if (this.appliedOffers.length > 0) {
        payload.applied_offer_ids = this.appliedOffers.map(o => o.id);

        const couponCodes = this.appliedOffers
          .filter(o => o.coupon_code)
          .map(o => o.coupon_code);

        if (couponCodes.length > 0) {
          payload.coupon_code = couponCodes[0];
        }

        console.log('✅ Applied offers included in payload:', {
          offerIds: payload.applied_offer_ids,
          couponCode: payload.coupon_code || 'none'
        });
      }

      // ✅ DEBUG: Log exact payload before sending
      console.group('📤 EXACT PAYLOAD BEING SENT:');
      console.log(JSON.stringify(payload, null, 2));
      console.groupEnd();

      console.log('📤 Sending to API /web/cart/analyze');

      const response = await this.apiClient.post('/web/cart/analyze', payload);
      const data = response.data?.data || response.data;

      console.log('📥 API Response:', {
        status: response.status,
        appliedOffers: data.applied_offers?.length || 0,
        autoApplyOffers: data.auto_apply_offers?.length || 0,
        totalDiscount: data.discount || 0
      });

      this.cartAnalysis = data;

      await this._syncWithApiResponse(data, cartItems);

      this.notifyListeners();

      console.log('✅ Cart analysis complete');
      console.groupEnd();
      return data;
    } catch (error) {
      console.error('❌ Cart analysis error:', error);
      this.clearOffers();
      this.notifyListeners();
      console.groupEnd();
      return null;
    }
  }

  async _scheduleReanalysis(cartItems, branchId, customerId, options = {}) {
    if (this._pendingAnalysis) {
      clearTimeout(this._pendingAnalysis);
    }

    return new Promise((resolve) => {
      this._pendingAnalysis = setTimeout(async () => {
        this._pendingAnalysis = null;
        const result = await this.analyzeCart(cartItems, branchId, customerId, options);
        resolve(result);
      }, this._analysisDebounceMs);
    });
  }

  setActiveOffers(offers) {
    this.activeOffersReference = offers.map(offer => ({
      id: offer.id,
      stackable: Boolean(offer.stackable),
      type: offer.offer_type,
      coupon_code: offer.coupon_code
    }));

    console.log('✅ Stored active offers reference:', this.activeOffersReference.length);
  }

  _getOfferStackability(offerId) {
    if (!this.activeOffersReference) {
      return false;
    }

    const offerRef = this.activeOffersReference.find(o => o.id === offerId);
    return offerRef ? offerRef.stackable : false;
  }

  async _syncWithApiResponse(data, cartItems) {
    console.log('🔄 Syncing with API response...');

    const previousManualOffers = [...this.appliedOffers];
    this.appliedOffers = [];
    this.autoAppliedOffers = [];
    this.availableOffers = [];
    this._appliedOfferIds.clear();

    if (data.auto_apply_offers && data.auto_apply_offers.length > 0) {
      const autoOffers = data.auto_apply_offers
        .filter(o => !this.removedAutoOfferIds.has(o.id))
        .map(o => this._normalizeOffer(o));

      for (const offer of autoOffers) {
        await this._applyOfferByType(offer, cartItems);
        this.autoAppliedOffers.push(offer);
        this._appliedOfferIds.add(offer.id);
      }

      console.log('✅ Auto-applied offers:', this.autoAppliedOffers.length);
    }

    // ✅ FIXED: Keep ALL manually applied offers regardless of API response
    // Manual offers persist until user explicitly removes them
    if (previousManualOffers.length > 0) {
      for (const manualOffer of previousManualOffers) {
        console.log('✅ Re-applying manual offer (persistent):', manualOffer.name, 'Discount: ₹' + manualOffer.discount_amount);
        await this._applyOfferByType(manualOffer, cartItems);

        // ✅ FIXED: If offer doesn't have discount_amount, try to get it from API response
        let offerToAdd = manualOffer;
        if (!manualOffer.discount_amount || manualOffer.discount_amount === 0) {
          // Look in the API response for this offer's details
          const apiOffer = data.applied_offers?.find(o => o.id === manualOffer.id) ||
            data.offers?.find(o => o.id === manualOffer.id) ||
            data.auto_apply_offers?.find(o => o.id === manualOffer.id);

          if (apiOffer && apiOffer.discount_amount) {
            offerToAdd = { ...manualOffer, discount_amount: parseFloat(apiOffer.discount_amount) };
            console.log('✅ Updated discount from API:', offerToAdd.discount_amount);
          }
        }

        this.appliedOffers.push(offerToAdd);
        this._appliedOfferIds.add(offerToAdd.id);
      }
    }

    if (data.offers && data.offers.length > 0) {
      this.availableOffers = data.offers
        .filter(o => !this._appliedOfferIds.has(o.id))
        .map(o => this._normalizeOffer(o));
    }

    if (this.removedAutoOfferIds.size > 0 && data.auto_apply_offers) {
      const blockedAutoOffers = data.auto_apply_offers
        .filter(o => this.removedAutoOfferIds.has(o.id))
        .map(o => this._normalizeOffer(o));

      blockedAutoOffers.forEach(blockedOffer => {
        if (!this.availableOffers.some(o => o.id === blockedOffer.id)) {
          this.availableOffers.push(blockedOffer);
        }
      });
    }

    console.log('📋 Sync complete - Applied:', this._appliedOfferIds.size, 'Available:', this.availableOffers.length);
  }

  async _applyOfferByType(offer, cartItems) {
    console.log(`🎯 Applying offer type: ${offer.type}`, offer.name);

    switch (offer.type) {
      case 'percentage':
      case 'fixed_amount':
      case 'minimum_order':
      case 'first_order':
      case 'tiered_discount':
        this._applyDiscountOffer(offer);
        break;

      case 'bogo':
        this._applyBogoOffer(offer);
        break;

      case 'buy_x_get_y':
        this._applyBuyXGetYOffer(offer);
        break;

      case 'free_item':
        this._applyFreeItemOffer(offer);
        break;

      case 'combo':
        this._applyComboOffer(offer);
        break;

      case 'category_discount':
        this._applyCategoryDiscountOffer(offer);
        break;

      default:
        console.warn('⚠️ Unknown offer type:', offer.type);
    }
  }

  _applyDiscountOffer(offer) {
    if (offer.discount_amount > 0) {
      console.log(`💰 Applied discount: ${offer.name} - ₹${offer.discount_amount}`);
    }
  }

  _applyBogoOffer(offer) {
    console.log(`🎉 Applied BOGO: ${offer.name}`);

    if (this.showNotification) {
      const freeItemsCount = offer.free_items?.length || 0;
      const freeItemNames = offer.free_items?.map(item => item.name).join(', ') || '';

      this.showNotification({
        type: 'success',
        title: '🎉 BOGO Applied!',
        message: `${offer.name}\n🎁 Free: ${freeItemNames}`,
        duration: 4000
      });
    }
  }

  _applyBuyXGetYOffer(offer) {
    console.log(`🎁 Applied Buy X Get Y: ${offer.name}`);
  }

  _applyFreeItemOffer(offer) {
    console.log(`🎁 Applied Free Item: ${offer.name}`);
  }

  _applyComboOffer(offer) {
    console.log(`🍔 Applied Combo: ${offer.name}`);
  }

  _applyCategoryDiscountOffer(offer) {
    console.log(`🏷️ Applied Category Discount: ${offer.name}`);
  }

  async validateAndApplyCoupon(couponCode, cartItems, branchId, customerId) {
    if (!couponCode || couponCode.trim() === '') {
      return {
        success: false,
        message: 'Please enter a coupon code'
      };
    }

    try {
      const items = this._prepareCartItems(cartItems);
      const subtotal = this._calculateSubtotal(cartItems);

      const payload = {
        coupon_code: couponCode.trim(),
        branch_id: Number(branchId),
        subtotal: subtotal,
        items: items,
        customer_id: Number(customerId)
      };

      console.log('🎟️ Validating coupon:', payload);

      const response = await this.apiClient.post('/web/offers/validate-coupon', payload);
      const result = response.data;

      if (result.status && result.data?.is_valid) {
        const offerData = result.data;

        const offer = this._normalizeOffer({
          id: offerData.offer_id,
          offer_id: offerData.offer_id,
          name: offerData.offer_name,
          offer_name: offerData.offer_name,
          type: offerData.offer_type,
          offer_type: offerData.offer_type,
          coupon_code: couponCode.trim(),
          discount_amount: offerData.discount_amount,
          free_items: offerData.free_items || [],
          applied_items: offerData.applied_items || [],
          stackable: offerData.stackable,
          message: offerData.message
        });

        const canApply = this._canApplyOffer(offer);

        if (!canApply.allowed) {
          return {
            success: false,
            message: canApply.reason
          };
        }

        await this._applyOfferByType(offer, cartItems);

        this.appliedOffers.push(offer);
        this._appliedOfferIds.add(offer.id);

        this.availableOffers = this.availableOffers.filter(o => o.id !== offer.id);

        await this._scheduleReanalysis(cartItems, branchId, customerId);

        this.notifyListeners();

        return {
          success: true,
          message: result.data.message || 'Coupon applied successfully',
          offer: offer
        };
      } else {
        return {
          success: false,
          message: result.message || 'Invalid coupon code'
        };
      }
    } catch (error) {
      console.error('❌ Coupon validation error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to validate coupon'
      };
    }
  }

  _canApplyOffer(newOffer) {
    if (this.appliedOffers.length === 0 && this.autoAppliedOffers.length === 0) {
      return { allowed: true };
    }

    const allApplied = [...this.appliedOffers, ...this.autoAppliedOffers];
    const hasNonStackable = allApplied.some(o => !o.stackable);

    if (hasNonStackable) {
      const nonStackableOffer = allApplied.find(o => !o.stackable);
      return {
        allowed: false,
        reason: `You already have "${nonStackableOffer.name}" applied, which cannot be combined with other offers.`
      };
    }

    if (!newOffer.stackable && allApplied.length > 0) {
      return {
        allowed: false,
        reason: `This offer cannot be combined with other offers. Please remove existing offers first.`
      };
    }

    return { allowed: true };
  }

  async applyOffer(offer, cartItems, branchId, customerId) {
    console.log('🎁 [OffersManager.applyOffer] Starting offer application:', {
      offerId: offer.id,
      offerName: offer.name || offer.offer_name,
      offerType: offer.type || offer.offer_type,
      discountAmount: offer.discount_amount,
      cartItemsCount: cartItems?.length || 0
    });

    if (this._isOfferApplied(offer.id)) {
      console.log('⚠️ [OffersManager.applyOffer] Offer already applied:', offer.id);
      return {
        success: false,
        message: 'Offer already applied'
      };
    }

    const canApply = this._canApplyOffer(offer);
    if (!canApply.allowed) {
      console.log('❌ [OffersManager.applyOffer] Cannot apply offer:', canApply.reason);
      return {
        success: false,
        message: canApply.reason
      };
    }

    console.log('✅ [OffersManager.applyOffer] Offer can be applied, proceeding...');

    await this._applyOfferByType(offer, cartItems);

    const normalizedOffer = this._normalizeOffer(offer);
    this.appliedOffers.push(normalizedOffer);
    this._appliedOfferIds.add(offer.id);

    console.log('📋 [OffersManager.applyOffer] Offer added to appliedOffers:', {
      totalApplied: this.appliedOffers.length,
      normalizedOffer: {
        id: normalizedOffer.id,
        name: normalizedOffer.name,
        discount: normalizedOffer.discount_amount
      }
    });

    this.availableOffers = this.availableOffers.filter(o => o.id !== offer.id);

    console.log('🔄 [OffersManager.applyOffer] Running cart re-analysis immediately...');
    // ✅ FIXED: Run re-analysis synchronously (don't debounce) for manual offer application
    await this.analyzeCart(cartItems, branchId, customerId);

    console.log('📢 [OffersManager.applyOffer] Notifying listeners after analysis...');
    this.notifyListeners();

    console.log('✅ [OffersManager.applyOffer] Offer application complete:', {
      offerId: offer.id,
      status: 'success',
      appliedOffersCount: this.appliedOffers.length,
      totalAppliedIds: Array.from(this._appliedOfferIds)
    });

    return {
      success: true,
      message: `${offer.name} applied successfully`
    };
  }

  async removeOffer(offerId, cartItems, branchId, customerId) {
    const removedManualOffer = this.appliedOffers.find(o => o.id === offerId);
    const removedAutoOffer = this.autoAppliedOffers.find(o => o.id === offerId);

    if (!removedManualOffer && !removedAutoOffer) {
      return {
        success: false,
        message: 'Offer not found'
      };
    }

    const removedOffer = removedManualOffer || removedAutoOffer;

    if (removedManualOffer) {
      this.appliedOffers = this.appliedOffers.filter(o => o.id !== offerId);
      this._appliedOfferIds.delete(offerId);
      console.log('🗑️ Removed manually applied offer:', removedManualOffer.name);
    }

    if (removedAutoOffer) {
      this.autoAppliedOffers = this.autoAppliedOffers.filter(o => o.id !== offerId);
      this._appliedOfferIds.delete(offerId);
      this.removedAutoOfferIds.add(offerId);
      console.log('🚫 Blocked auto-applied offer:', removedAutoOffer.name);
    }

    console.log('🔄 Running cart re-analysis immediately after offer removal...');
    // ✅ FIXED: Run re-analysis synchronously for manual offer removal too
    await this.analyzeCart(cartItems, branchId, customerId);
    this.notifyListeners();

    return {
      success: true,
      message: `${removedOffer.name} removed successfully`
    };
  }

  getTotalSavings() {
    let total = 0;
    const discountTypes = [
      'percentage',
      'fixed_amount',
      'minimum_order',
      'category_discount',
      'first_order',
      'tiered_discount',
      'combo'
    ];

    const allOffers = [...this.autoAppliedOffers, ...this.appliedOffers];

    // ✅ DEBUG: Log all offers  with their types and discounts
    if (allOffers.length > 0) {
      console.group('💰 getTotalSavings() analysis');
      for (const offer of allOffers) {
        const isIncluded = discountTypes.includes(offer.type);
        console.log(`Offer: ${offer.name}, Type: ${offer.type}, Discount: ₹${offer.discount_amount}, Included: ${isIncluded}`);
        if (isIncluded) {
          total += offer.discount_amount;
        }
      }
      console.log(`Total Savings: ₹${total}`);
      console.groupEnd();
    }

    return total;
  }

  getFreeItemsValue() {
    let total = 0;
    const allOffers = [...this.autoAppliedOffers, ...this.appliedOffers];

    for (const offer of allOffers) {
      if (offer.free_items && offer.free_items.length > 0) {
        for (const item of offer.free_items) {
          const price = parseFloat(item.price || item.value || 0);
          const quantity = parseInt(item.quantity || 1);
          total += price * quantity;
        }
      }
    }

    return total;
  }

  getAllAppliedOffers() {
    return [...this.autoAppliedOffers, ...this.appliedOffers];
  }

  getOrderPayloadData() {
    const allApplied = this.getAllAppliedOffers();

    if (allApplied.length === 0) {
      return null;
    }

    return {
      applied_offer_ids: allApplied.map(o => o.id),
      total_offer_discount: this.getTotalSavings(),
      applied_offers: allApplied.map(o => ({
        offer_id: o.id,
        offer_name: o.name,
        offer_type: o.type,
        discount_amount: o.discount_amount,
        coupon_code: o.coupon_code || null
      }))
    };
  }

  clearOffers() {
    this.appliedOffers = [];
    this.availableOffers = [];
    this.autoAppliedOffers = [];
    this.cartAnalysis = null;
    this._appliedOfferIds.clear();
    this._preparedCartCache = null;
    this._cartCacheKey = null;
    this.removedAutoOfferIds.clear();

    if (this._pendingAnalysis) {
      clearTimeout(this._pendingAnalysis);
      this._pendingAnalysis = null;
    }

    console.log('🧹 Cleared all offers');
    this.notifyListeners();
  }

  _isOfferApplied(offerId) {
    return this._appliedOfferIds.has(offerId);
  }

  _normalizeOffer(offer) {
    let stackable = false;

    if (offer.stackable !== undefined && offer.stackable !== null) {
      stackable = Boolean(offer.stackable);
    }
    else if (this.activeOffersReference && (offer.id || offer.offer_id)) {
      stackable = this._getOfferStackability(offer.id || offer.offer_id);
    }
    else {
      const nonStackableTypes = ['bogo', 'buy_x_get_y', 'combo', 'free_item'];
      const stackableTypes = ['tiered_discount'];
      const offerType = offer.type || offer.offer_type;

      if (nonStackableTypes.includes(offerType)) {
        stackable = false;
      } else if (stackableTypes.includes(offerType)) {
        stackable = true;
      } else if (offer.coupon_code) {
        stackable = false;
      } else {
        stackable = true;
      }
    }

    return {
      id: offer.id || offer.offer_id,
      name: offer.name || offer.offer_name,
      type: offer.type || offer.offer_type || 'percentage',
      coupon_code: offer.coupon_code || null,
      discount_amount: parseFloat(offer.discount_amount || offer.savings_amount || 0),
      free_items: offer.free_items || [],
      applied_items: offer.applied_items || [],
      stackable: stackable,
      message: offer.message || offer.user_message || '',
      display_text: offer.display_text || offer.display_description || ''
    };
  }

  _logCartPricing(cartItems) {
    console.group('🔍 [OffersManager] Cart Pricing Breakdown');

    cartItems.forEach((item, index) => {
      const variations = Array.isArray(item.variations) ? item.variations : [];
      const extras = Array.isArray(item.extras) ? item.extras : [];
      const addons = Array.isArray(item.addons) ? item.addons : [];

      let itemPrice = 0;

      console.log(`Item ${index + 1}: ${item.name || item.id}`);

      // ✅ CRITICAL: If flatPrice exists, use it directly (it's pre-calculated)
      if (item.flatPrice && parseFloat(item.flatPrice) > 0) {
        itemPrice = parseFloat(item.flatPrice);
        console.log(`  ✅ Using PRE-CALCULATED flatPrice: ₹${itemPrice}`);
      } else {
        // Fallback: Calculate from components
        itemPrice = Number(item.price || 0);
        console.log(`  Base Price: ₹${itemPrice}`);

        if (variations.length > 0) {
          const highestVariationPrice = Math.max(
            ...variations.map(v => Number(v.variation_price || v.price || 0))
          );
          console.log(`  + Variation: ₹${highestVariationPrice}`);
          itemPrice += highestVariationPrice;
        }

        if (extras.length > 0) {
          const extraTotal = extras.reduce(
            (sum, e) => sum + Number(e.extra_price || e.price || 0),
            0
          );
          console.log(`  + Extras: ₹${extraTotal}`);
          itemPrice += extraTotal;
        }

        if (addons.length > 0) {
          const addonTotal = addons.reduce(
            (sum, a) => sum + Number(a.addon_item_price || a.price || 0),
            0
          );
          console.log(`  + Addons: ₹${addonTotal}`);
          itemPrice += addonTotal;
        }
      }

      console.log(`  Final Price: ₹${itemPrice} × ${item.quantity} = ₹${itemPrice * item.quantity}`);
    });

    console.groupEnd();
  }

  // ✅ CRITICAL FIX: Send combo items with item_id instead of combo_id
  _prepareCartItems(cartItems) {

    const regularItems = cartItems.filter(item => !item.isCombo && !item.isComboOffer);
    const cacheKey = JSON.stringify(cartItems.map(i => ({
      id: i.id,
      q: i.quantity,
      p: i.flatPrice || i.price,
      v: i.variations?.length || 0,
      e: i.extras?.length || 0,
      a: i.addons?.length || 0,
      isCombo: i.isCombo || false
    })));

    // ✅ CRITICAL FIX: Cache was causing stale prices
    // Only return cached result if cart structure AND items are unchanged
    if (this._cartCacheKey === cacheKey && this._preparedCartCache) {
      console.log('📦 Using cached cart items (structure unchanged)');
      return this._preparedCartCache;
    }

    const prepared = cartItems.map(item => {
      // ✅ CRITICAL FIX: Combo items now use item_id (not combo_id)
      if (item.isCombo || item.isComboOffer) {
        const comboPrice = Number(item.flatPrice || item.price || 0);
        const comboTotal = comboPrice * Number(item.quantity);

        // ✅ FIX: Two different things both end up with isCombo:true on the
        // cart item, and only one of them is a real "offer combo":
        //  - Offer-based combos (RestaurantMenuApp addToCart's
        //    product.comboData path) carry a genuine comboOfferId/
        //    isComboOffer pointing at a real Offer record — these must keep
        //    sending offer_id exactly as before.
        //  - Fixed/bundle products (added via ComboDetailsModal's
        //    comboComponents path, e.g. a "pizza 220" bundle item) are
        //    ordinary catalog items that merely display their contents —
        //    they never receive a comboOfferId. Previously these fell
        //    through to `comboOfferId = Number(item.id)` and were sent as
        //    `offer_id`, i.e. the item's own catalog item_id mislabeled as
        //    an offer id. The backend can't resolve that as a real offer,
        //    which broke /web/cart/analyze (and therefore shipping/delivery
        //    fee) for the ENTIRE cart whenever one of these bundles was
        //    present. Send it as a normal item line instead — its actual
        //    catalog identity — since that's what it really is.
        const isRealOfferCombo = Boolean(item.comboOfferId) || item.isComboOffer === true;

        if (!isRealOfferCombo) {
          console.log(`📦 [OffersManager] Fixed bundle product (not an offer): ${item.name}, item_id: ${item.id}, Price: ₹${comboPrice}, Total: ₹${comboTotal}`);
          return {
            item_id: parseInt(item.id, 10),
            quantity: Number(item.quantity),
            price: Number(comboPrice.toFixed(2)),
            total_price: Number(comboTotal.toFixed(2))
          };
        }

        // ✅ Extract the actual offer ID
        let comboOfferId;
        if (item.comboOfferId) {
          comboOfferId = Number(item.comboOfferId);
        } else if (typeof item.id === 'string' && item.id.startsWith('combo-')) {
          comboOfferId = Number(item.id.replace('combo-', ''));
        } else {
          comboOfferId = Number(item.id);
        }

        console.log(`🎁 [OffersManager] Combo: ${item.name}, Offer ID: ${comboOfferId}, Price: ₹${comboPrice}, Total: ₹${comboTotal}`);

        // ✅ FIXED: Use item_id (API requirement) instead of combo_id
        return {
          offer_id: comboOfferId,  // ← Changed from combo_id to item_id
          quantity: Number(item.quantity),
          price: Number(comboPrice.toFixed(2)),
          total_price: Number(comboTotal.toFixed(2)),
          is_combo: true
        };
      }

      // ✅ Regular items
      const variations = Array.isArray(item.variations) ? item.variations : [];
      const extras = Array.isArray(item.extras) ? item.extras : [];
      const addons = Array.isArray(item.addons) ? item.addons : [];

      let itemPrice = 0;

      // ✅ DEBUG: Log the decision process
      console.group(`📦 Preparing Item: ${item.name} (ID: ${item.id})`);
      console.log('Item properties:', {
        price: item.price,
        flatPrice: item.flatPrice,
        variations: variations.length,
        extras: extras.length,
        addons: addons.length
      });

      // ✅ CRITICAL: If flatPrice exists, ALWAYS use it (it's pre-calculated with all customizations)
      if (item.flatPrice && parseFloat(item.flatPrice) > 0) {
        itemPrice = parseFloat(item.flatPrice);
        console.log(`✅ Using PRE-CALCULATED flatPrice: ₹${itemPrice}`);
      } else {
        // Fallback: Calculate from scratch when no flatPrice
        const basePrice = parseFloat(item.price || 0);
        itemPrice = basePrice;
        console.log(`📌 Using base price: ₹${basePrice}`);

        // Add variation price if exists
        if (variations.length > 0) {
          const highestVariationPrice = Math.max(
            ...variations.map(v => Number(v.variation_price || v.price || 0))
          );
          itemPrice = basePrice + highestVariationPrice;
          console.log(`  + Variation: ₹${highestVariationPrice} → Total: ₹${itemPrice}`);
        }

        // Add extras
        const extraTotal = extras.reduce(
          (sum, e) => sum + Number(e.extra_price || e.price || 0),
          0
        );
        if (extraTotal > 0) {
          itemPrice += extraTotal;
          console.log(`  + Extras: ₹${extraTotal} → Total: ₹${itemPrice}`);
        }

        // Add addons
        const addonTotal = addons.reduce(
          (sum, a) => sum + Number(a.addon_item_price || a.price || 0),
          0
        );
        if (addonTotal > 0) {
          itemPrice += addonTotal;
          console.log(`  + Addons: ₹${addonTotal} → Total: ₹${itemPrice}`);
        }
      }

      const totalPrice = itemPrice * Number(item.quantity);
      console.log(`✅ FINAL: Price ₹${itemPrice} × Qty ${item.quantity} = ₹${totalPrice}`);
      console.groupEnd();

      const cartItem = {
        item_id: parseInt(item.id),
        quantity: parseInt(item.quantity),
        price: Number(itemPrice.toFixed(2)),
        total_price: Number(totalPrice.toFixed(2))
      };

      // Include weight_info for weight-based items so the backend can calculate
      // delivery fee without a DB lookup.
      if (item.isWeightBased && item.selectedWeight) {
        const weightKg = Number(item.selectedWeight || 0);
        const cartQty = Number(item.quantity || 1);
        const totalWKg = Number((weightKg * cartQty).toFixed(3));
        if (cartQty > 1) {
          cartItem.weight_info = `${totalWKg} kg (${weightKg} × ${cartQty})`;
        } else if (weightKg < 1) {
          cartItem.weight_info = `${Math.round(weightKg * 1000)} g`;
        } else {
          cartItem.weight_info = `${weightKg} kg`;
        }
      }

      return cartItem;
    });

    this._preparedCartCache = prepared;
    this._cartCacheKey = cacheKey;

    return prepared;
  }

  // ✅ Calculate subtotal with proper flatPrice handling
  _calculateSubtotal(cartItems) {
    const subtotal = cartItems.reduce((sum, item) => {
      if (item.isCombo || item.isComboOffer) {
        const comboPrice = Number(item.flatPrice || item.price || 0);
        const comboTotal = comboPrice * Number(item.quantity);
        return sum + comboTotal;
      }

      const variations = Array.isArray(item.variations) ? item.variations : [];
      const extras = Array.isArray(item.extras) ? item.extras : [];
      const addons = Array.isArray(item.addons) ? item.addons : [];

      let itemPrice = 0;

      // ✅ CRITICAL: If flatPrice exists, use it directly (it's pre-calculated)
      if (item.flatPrice && parseFloat(item.flatPrice) > 0) {
        itemPrice = Number(item.flatPrice);
      } else {
        // Fallback: Calculate from base price
        const basePrice = Number(item.price || 0);
        itemPrice = basePrice;

        // Add variation price if exists
        if (variations.length > 0) {
          const highestVariationPrice = Math.max(
            ...variations.map(v => Number(v.variation_price || v.price || 0))
          );
          itemPrice = basePrice + highestVariationPrice;
        }

        // Add extras
        const extraTotal = extras.reduce(
          (total, e) => total + Number(e.extra_price || e.price || 0),
          0
        );
        itemPrice += extraTotal;

        // Add addons
        const addonTotal = addons.reduce(
          (total, a) => total + Number(a.addon_item_price || a.price || 0),
          0
        );
        itemPrice += addonTotal;
      }

      const itemTotal = itemPrice * Number(item.quantity);
      return sum + itemTotal;
    }, 0);

    console.log(`💰 [OffersManager] Calculated subtotal: ₹${subtotal.toFixed(2)}`);

    return Number(subtotal.toFixed(2));
  }
}

export default OffersManager;